import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const ServiceDetails = ({ service }) => {
  const [activeTab, setActiveTab] = useState('overview');

  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'FileText' },
    { id: 'deliverables', label: 'Deliverables', icon: 'Package' },
    { id: 'documents', label: 'Documents Required', icon: 'FileCheck' },
    { id: 'process', label: 'Process', icon: 'GitBranch' }
  ];

  return (
    <div className="bg-card rounded-xl shadow-md border border-border overflow-hidden">
      <div className="border-b border-border overflow-x-auto">
        <div className="flex gap-1 p-2 min-w-max">
          {tabs?.map((tab) => (
            <button
              key={tab?.id}
              onClick={() => setActiveTab(tab?.id)}
              className={`flex items-center gap-2 px-4 py-3 rounded-lg text-sm font-medium transition-all duration-250 whitespace-nowrap ${
                activeTab === tab?.id
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:bg-muted'
              }`}
            >
              <Icon name={tab?.icon} size={18} />
              <span>{tab?.label}</span>
            </button>
          ))}
        </div>
      </div>
      <div className="p-6 md:p-8">
        {activeTab === 'overview' && (
          <div className="space-y-6">
            <div>
              <h3 className="text-xl font-semibold text-foreground mb-3">Service Overview</h3>
              <p className="text-muted-foreground leading-relaxed">{service?.detailedDescription}</p>
            </div>

            <div>
              <h4 className="text-lg font-semibold text-foreground mb-3">Key Features</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {service?.keyFeatures?.map((feature, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-success/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Icon name="Check" size={14} className="text-success" />
                    </div>
                    <span className="text-sm text-foreground">{feature}</span>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <h4 className="text-lg font-semibold text-foreground mb-3">Who Should Use This Service</h4>
              <div className="flex flex-wrap gap-2">
                {service?.targetAudience?.map((audience, index) => (
                  <span
                    key={index}
                    className="px-3 py-1.5 rounded-lg bg-muted text-foreground text-sm"
                  >
                    {audience}
                  </span>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'deliverables' && (
          <div className="space-y-4">
            <h3 className="text-xl font-semibold text-foreground mb-4">What You'll Receive</h3>
            <div className="space-y-3">
              {service?.deliverables?.map((deliverable, index) => (
                <div key={index} className="flex items-start gap-4 p-4 rounded-lg bg-muted">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Icon name="CheckCircle" size={20} className="text-primary" />
                  </div>
                  <div className="flex-1">
                    <h5 className="font-semibold text-foreground mb-1">{deliverable?.title}</h5>
                    <p className="text-sm text-muted-foreground">{deliverable?.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'documents' && (
          <div className="space-y-4">
            <h3 className="text-xl font-semibold text-foreground mb-4">Required Documents</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {service?.requiredDocuments?.map((doc, index) => (
                <div key={index} className="flex items-start gap-3 p-4 rounded-lg border border-border">
                  <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center flex-shrink-0">
                    <Icon name="FileText" size={20} className="text-accent" />
                  </div>
                  <div className="flex-1">
                    <h5 className="font-semibold text-foreground mb-1">{doc?.name}</h5>
                    <p className="text-xs text-muted-foreground caption mb-2">{doc?.description}</p>
                    {doc?.required && (
                      <span className="inline-flex items-center gap-1 text-xs text-error">
                        <Icon name="AlertCircle" size={12} />
                        Required
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'process' && (
          <div className="space-y-6">
            <h3 className="text-xl font-semibold text-foreground mb-4">Service Process</h3>
            <div className="relative">
              {service?.processSteps?.map((step, index) => (
                <div key={index} className="flex gap-4 pb-8 last:pb-0">
                  <div className="flex flex-col items-center">
                    <div className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-semibold text-sm flex-shrink-0">
                      {index + 1}
                    </div>
                    {index < service?.processSteps?.length - 1 && (
                      <div className="w-0.5 h-full bg-border mt-2"></div>
                    )}
                  </div>
                  <div className="flex-1 pt-1">
                    <h5 className="font-semibold text-foreground mb-2">{step?.title}</h5>
                    <p className="text-sm text-muted-foreground mb-2">{step?.description}</p>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground caption">
                      <Icon name="Clock" size={14} />
                      <span>{step?.duration}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ServiceDetails;